/**
 * Types and pure helpers for the review workspace.
 *
 * The domain object is a `Review` of a printed label against its approved
 * master. Each `Finding` carries four independent axes; the axes must be
 * modelled and rendered independently. Collapsing them into a single
 * status chip loses meaningful distinctions — confirmed-and-corrected,
 * confirmed-and-open, and dismissed-and-disputed are all legitimate
 * combined states, and the client report is gated on the intersection.
 *
 * This module owns the UI types and format-agnostic helpers. The API layer
 * (`api/`) builds on top of it via the wire→UI adapter; feature components
 * import types from here but never mutate them.
 */

import {
  type DetectorProfile,
  type DetectorProfileResolved,
} from "@/components/domain/detector-profile"
import { type Role } from "@/lib/roles"
import type { components } from "@/types/api.gen"

export type ReviewId = string & { __brand: "ReviewId" }
export type FindingId = string & { __brand: "FindingId" }
export type MessageId = string & { __brand: "MessageId" }
export type UserId = string & { __brand: "UserId" }

/** Severity is the rule's own reading — how bad it is if the divergence is real. */
export type FindingSeverity = "error" | "warning" | "info"

/**
 * The four independent axes that describe a finding's lifecycle. Each
 * axis moves on its own; consumers must render them independently. The
 * spine primitive shows all four as parallel bars in the record's gutter.
 */
export interface FindingAxes {
  /** Is it real? Reviewer/specialist call. */
  verdict: "unset" | "confirmed" | "rejected" | "needs_sme"
  /** Is it fixed? Pipeline proposes candidate_corrected; a human confirms. */
  state: "open" | "candidate_corrected" | "corrected_confirmed" | "resolved"
  /** Did the client agree? Only meaningful once the client sees the report. */
  clientResolution: "none" | "acknowledged" | "disputed" | "done"
  /** Where did it come from? Set at creation; never changes. */
  origin: "new" | "carried_over" | "regression" | "manual"
}

/** Where the finding sits on the artefact. Drives whether an overlay box is drawn. */
export type FindingPlacement = "box" | "page" | "document"

/**
 * A finding's location expressed in PDF point space. Storing points (not
 * pixels) is what lets zoom, DPR, and container resize compose without
 * per-consumer math; the surface converts to CSS pixels at draw time.
 */
export interface PointRect {
  page: number
  x0: number
  y0: number
  x1: number
  y1: number
}

/** Two-sided evidence: what the master says vs. what the printed label shows. */
export interface Evidence {
  master: string | null
  printed: string | null
  /**
   * The pipeline's own sentence naming what differs.
   *
   * Distinct from `reasoning`, which argues why the difference is a violation.
   * This states the difference itself, and is what a reviewer reads before
   * comparing the two quoted texts below it.
   */
  statement: string | null
}

/** A regulatory citation. `kind` selects the resolver (eCFR, LRM chapter, etc.). */
export interface Authority {
  kind: "statute" | "cfr" | "lrm" | "prn" | "internal"
  label: string
  href?: string
}

/**
 * Backend model: Comment. `authorSide` is derived from the principal
 * server-side and cannot be forged from the wire. Visibility is not a
 * per-message toggle — every comment on a review is equally visible to
 * every side that has access.
 */
export interface FindingComment {
  id: MessageId
  authorName: string
  authorSide: AuthorSide
  body: string
  at: string
}

/** Backend enum. `ai` is the pipeline-authored side, not a human role. */
export const AuthorSide = {
  REVIEWER: "reviewer",
  CLIENT: "client",
  AI: "ai",
} as const
export type AuthorSide = (typeof AuthorSide)[keyof typeof AuthorSide]

/** KPI grouping — which "check group" this finding rolls up to on the report. */
export interface FindingGroup {
  id: string
  label: string
  ordinal: number
}

export interface Finding {
  id: FindingId
  /** Display number — position in the label. Recomputed on the server. */
  number: number
  ruleId: string
  /**
   * The label section this finding sits in (`precautionary`, `first_aid`),
   * straight from the backend's DD-03 taxonomy. Null where the pipeline could
   * not place it.
   *
   * It is what tells two findings of the same rule apart: a review commonly
   * carries four TXT-01s, and the rule id alone names all four identically.
   */
  sectionId: string | null
  severity: FindingSeverity
  claim: string
  /** True when a reviewer replaced the generated wording. */
  claimIsOverridden: boolean
  placement: FindingPlacement
  /** Only present when placement = "box"; overlay reads this. */
  bbox: PointRect | null
  /** For placement = "page"; the page the callout ties to. */
  pageHint: number | null
  occurrences: number
  /**
   * Every printed-label location of this finding, in the pipeline's order.
   *
   * `bbox` is the first of these; the rest are the repeats ("6× on label")
   * the client report steps through. Empty when nothing on the label was
   * located.
   */
  occurrenceRects: PointRect[]
  axes: FindingAxes
  /** Pipeline's own confidence, 0–1. Absent on manually-authored findings. */
  confidence: number | null
  /**
   * The KPIs this finding counts against (DD-03), most significant first.
   *
   * A rule can serve more than one, which is why this is a list: TXT-01 feeds
   * both wording accuracy and template conformance. Empty for a finding a
   * reviewer wrote by hand, which answers to no KPI.
   */
  kpis: string[]
  authoredBy: { name: string; at: string } | null
  group: FindingGroup
  kind: "mechanical" | "judgment" | "hybrid"
  divergence: Evidence | null
  reasoning: string | null
  remediation: string | null
  authorities: Authority[]
  /** Version the finding was first seen in. Drives the "carried over from v2" chip. */
  firstSeenVersion: string
  commentCount: number
  unreadCount: number
  smeNote: { by: string; at: string; note: string } | null
  dismissalNote: { by: string; at: string; note: string | null } | null
}

/**
 * Backend workflow stage. Three stages only — this mirrors
 * ReviewWorkflowStage in the API. Archived is orthogonal (a boolean on
 * the review, not a stage) because an archived review preserves its last
 * stage and can be un-archived back into the queue.
 */
export type ReviewStage = "initial" | "secondary" | "done"

/**
 * The set of actions the assignment/routing endpoints can record. Every
 * hand-off is one of these; the trail below stores them verbatim so the
 * UI can render "reassigned from X to Y" as more than a stage change.
 */
export type AssignmentAction =
  "assign" | "reassign" | "throwback" | "advance_stage" | "skip_secondary" | "archive" | "unarchive"

/**
 * One entry in the append-only assignment trail. Mirrors the backend
 * AssignmentEvent shape. `toStage` is nullable because throwback and
 * (un)archive don't move the stage; `fromUser`/`toUser` may both be
 * null on stage advances that don't touch assignment.
 */
export interface AssignmentEvent {
  action: AssignmentAction
  at: string
  actor: string | null
  fromUser: string | null
  toUser: string | null
  toStage: ReviewStage | null
  note: string | null
  /** The actor did not hold the review — the server recorded it as an override. */
  positionOverride: boolean
}

export interface ReviewAssignment {
  currentStage: ReviewStage
  /** Server-side flag. Archived reviews keep their last stage. */
  archived: boolean
  /** True once secondary was explicitly skipped; drives the trail badge. */
  secondarySkipped: boolean
  /** Null = in the pool, waiting for someone to pick up. */
  assignee: { userId: UserId; name: string; role: Role; sinceISO: string } | null
  /** Append-only trail of every routing action. Rendered as History. */
  events: AssignmentEvent[]
}

// Role vocabulary lives in `lib/roles`; re-exported so existing imports keep working.
// Only reviewer/sme/admin/viewer are assignable — a client is a counterparty on the
// engagement, never a review holder.
export type { Role } from "@/lib/roles"

export interface ReviewClientAttachment {
  clientId: string
  clientName: string
  memberCount: number
}

/**
 * Backend Comment, projected for the UI. `authorSide` is the server's
 * derivation of who authored it — never trusted from the client. There is
 * no `internal` flag: comments are equally visible to every side that has
 * access to the review. If we ever need reviewer-only notes, they will be
 * a separate resource, not a boolean on this one.
 */
export interface Message {
  id: MessageId
  authorName: string
  authorSide: AuthorSide
  body: string
  at: string
  /** Null = review-level; otherwise attached to a specific finding. */
  findingId: FindingId | null
  /** True when the current principal authored it. Client-side derived. */
  mine: boolean
  /** True when this reader has not seen it. Per-side, from the server. */
  unread: boolean
  /**
   * Where this message is in its journey to the server. Absent on everything
   * the server sent — only a message this client is sending carries one.
   *
   * A failed message stays in the list rather than disappearing: it holds text
   * someone typed, and a review is a record where a silently dropped sentence
   * is worse than a visible failure.
   */
  status?: MessageStatus
}

/** A locally-composed message's delivery state. See `Message.status`. */
export type MessageStatus = "sending" | "failed"

export type LiveState = "live" | "reconnecting" | "offline"

/** Where the pipeline has got to. Null before it starts. */
export type PipelineStage = components["schemas"]["PipelineStage"]

export type ReviewStatus = components["schemas"]["ReviewStatus"]

/**
 * What each pipeline stage is called on screen.
 *
 * Keyed by the generated enum, so a stage added to the backend fails the build
 * here rather than rendering its raw name to a reviewer.
 */
export const PIPELINE_STAGE_LABEL: Record<PipelineStage, string> = {
  queued: "Queued",
  ingest: "Reading the documents",
  version_match: "Establishing the governing master",
  extract: "Reading the label",
  segment: "Finding the sections",
  align: "Comparing against the master",
  validate: "Checking the rules",
  judge: "Weighing the wording",
  batch_wait: "Waiting on the overnight lane",
  carryover: "Carrying findings forward",
  done: "Finishing",
  awaiting_input: "Waiting on an answer",
}

/** True while nothing has been decided yet, so an empty finding list means nothing. */
export function isRunning(status: ReviewStatus): boolean {
  return status === "pending" || status === "running"
}

export interface Review {
  id: ReviewId
  /** The review's own state, distinct from the workflow stage on `assignment`. */
  status: ReviewStatus
  /** Where the pipeline has got to, for the running banner. */
  pipelineStage: PipelineStage | null
  /**
   * Why the run stopped, in the server's words, for the blocker strip to print
   * verbatim. Null when nothing stopped it — and also, occasionally, when
   * something did: a parked review whose note did not arrive is still parked,
   * so the strip renders on `status` and treats this as detail it may lack.
   */
  ocrNote: string | null
  /** The detector settings asked for. Empty is Automatic throughout. */
  detectorProfileRequested: DetectorProfile
  /** What the run applied, and where each value came from. Null until the run resolves it. */
  detectorProfile: DetectorProfileResolved | null
  /**
   * Whether the unread pages carry any text. `null` means we were not told,
   * which is not the same as "there is none" — only an explicit `false` makes
   * including and excluding them compare the same thing.
   */
  unreadPagesHaveText: boolean | null
  /** Set when the review is in the trash. Admins alone can still open it. */
  trashedAt: string | null
  /** When the overnight lane expects an answer. Null before it is known. */
  batchExpectedBy: string | null
  /** The review this one corrects, when it was filed as a replacement. */
  previousReviewId: string | null
  epaRegNo: string
  productName: string
  productKind: string
  version: string
  fplName: string
  masterName: string
  fplUrl: string
  masterUrl: string
  /** When the printed label was printed, as submitted (ISO). Null when nobody said. */
  fplDate: string | null
  pageCount: number
  /**
   * The approved master this review was judged against, as the pipeline
   * resolved it (DD-07-lite). Null when no master was established — an upload
   * that never reached version matching, or a review still running.
   */
  governingMaster: GoverningMaster | null
  createdAt: string
  updatedAt: string
  assignment: ReviewAssignment
  client: ReviewClientAttachment | null
  findings: Finding[]
  messages: Message[]
}

/* ─── Pure helpers ─────────────────────────────────────────────────────
 * Deterministic and framework-free. Safe to call from render, tests, or
 * a Node script. Do not import React or DOM APIs into this section.
 */

/** Sorts findings within a KPI group by their display number, ascending. */
export function sortByNumber(findings: readonly Finding[]): Finding[] {
  return [...findings].sort((a, b) => a.number - b.number)
}

/**
 * Group findings by their KPI group, ordered by group.ordinal, and
 * numerically sorted within each group. Skips empty groups.
 */
export function groupFindings(findings: readonly Finding[]): Array<{
  group: FindingGroup
  items: Finding[]
}> {
  const map = new Map<string, { group: FindingGroup; items: Finding[] }>()
  for (const f of findings) {
    const entry = map.get(f.group.id)
    if (entry) entry.items.push(f)
    else map.set(f.group.id, { group: f.group, items: [f] })
  }
  return [...map.values()]
    .sort((a, b) => a.group.ordinal - b.group.ordinal)
    .map(({ group, items }) => ({ group, items: sortByNumber(items) }))
}

/** Count of findings still awaiting a verdict. */
export function undecidedCount(findings: readonly Finding[]): number {
  return findings.filter((f) => f.axes.verdict === "unset").length
}

/** Findings that carry no on-label location — rendered in a rail card, not the overlay. */
export function documentPlacementFindings(findings: readonly Finding[]): Finding[] {
  return findings.filter((f) => f.placement === "document")
}

/** Filters shown as saved-view chips in the rail. Each is a predicate. */
export type SavedViewId =
  "all" | "needs_decision" | "blocking_report" | "not_addressed" | "client_disputed"

export function matchesSavedView(view: SavedViewId, f: Finding): boolean {
  switch (view) {
    case "all":
      return true
    case "needs_decision":
      return f.axes.verdict === "unset"
    case "blocking_report":
      return f.axes.verdict === "needs_sme"
    case "not_addressed":
      // "Was there before, still open" — carried_over or regression AND state=open.
      return (
        f.axes.state === "open" &&
        (f.axes.origin === "carried_over" || f.axes.origin === "regression")
      )
    case "client_disputed":
      return f.axes.clientResolution === "disputed"
  }
}

/**
 * The master a review is judged against, and how confident that pick is.
 *
 * `needsSme` is the server's own escalation: it resolves the newest stamp on or
 * before the printed date, and says so when it had to fall back instead. The
 * reasons are its words, not ours — a reviewer deciding whether to trust the
 * comparison needs the server's account of why, not a paraphrase.
 */
export interface GoverningMaster {
  /** EPA registration number the master was found under. */
  regNo: string | null
  /** EPA's stamp date, `YYYY-MM-DD`, or null when the master carries none. */
  stampedOn: string | null
  /** EPA's own wording for the acceptance date, when it published one. */
  acceptedOn: string | null
  /** True when the pipeline could not pick cleanly and escalated. */
  needsSme: boolean
  /** Why the pick is not clean. Empty when it is. */
  reasons: string[]
  /** PPLS document this resolved to, for a reviewer who wants the source. */
  url: string | null
}

/** Free-text match across rule id, group label, and claim text. */
export function matchesSearch(f: Finding, query: string): boolean {
  const q = query.trim().toLowerCase()
  if (q.length === 0) return true
  return (
    f.ruleId.toLowerCase().includes(q) ||
    f.group.label.toLowerCase().includes(q) ||
    f.claim.toLowerCase().includes(q) ||
    String(f.number).includes(q)
  )
}

/** Human labels — the client-facing strings. Never mutate. */
export const SEVERITY_LABEL: Record<FindingSeverity, string> = {
  error: "Error",
  warning: "Warning",
  info: "Info",
}

export const VERDICT_LABEL: Record<FindingAxes["verdict"], string> = {
  unset: "Undecided",
  confirmed: "Confirmed",
  rejected: "Dismissed",
  needs_sme: "With a specialist",
}

export const STATE_LABEL: Record<FindingAxes["state"], string> = {
  open: "Open",
  candidate_corrected: "Correction proposed",
  corrected_confirmed: "Corrected",
  resolved: "Resolved",
}

export const CLIENT_LABEL: Record<FindingAxes["clientResolution"], string> = {
  none: "No client response",
  acknowledged: "Acknowledged",
  disputed: "Disputed",
  done: "Client resolved",
}

export const ORIGIN_LABEL: Record<FindingAxes["origin"], string> = {
  new: "New this version",
  carried_over: "Carried over",
  regression: "Regression",
  manual: "Reviewer added",
}

export const STAGE_LABEL: Record<ReviewStage, string> = {
  initial: "Initial",
  secondary: "Secondary",
  done: "Done",
}

export const STAGE_ORDER: readonly ReviewStage[] = ["initial", "secondary", "done"]

/**
 * Human labels for the assignment trail. Verbs, past tense — the trail
 * is a record of what happened, not a menu of what can happen.
 */
export const ACTION_LABEL: Record<AssignmentAction, string> = {
  assign: "Assigned",
  reassign: "Reassigned",
  throwback: "Returned to pool",
  advance_stage: "Advanced",
  skip_secondary: "Skipped secondary",
  archive: "Archived",
  unarchive: "Unarchived",
}

export { ROLE_LABEL } from "@/lib/roles"

export const AUTHOR_SIDE_LABEL: Record<AuthorSide, string> = {
  reviewer: "Reviewer",
  client: "Client",
  ai: "AI",
}
